import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, TrendingUp, CalendarCheck, Layers, Activity, Plus, PlayCircle,
  Trash2, CalendarDays, Sparkles, BookOpen, Loader2, Printer, Award, ClipboardCheck, Library, KeyRound, Copy,
  Send, Lightbulb, CheckCircle2, Clock, HelpCircle, ThumbsUp,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import Navbar from "@/components/Navbar";
import VRViewer from "@/components/VRViewer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { printLesson } from "@/lib/printLesson";

const STAT_CARDS = [
  { key: "total_students", label: "Students", icon: Users, color: "text-primary bg-primary/10", suffix: "" },
  { key: "avg_progress", label: "Avg progress", icon: TrendingUp, color: "text-emerald-600 bg-emerald-500/10", suffix: "%" },
  { key: "avg_quiz_score", label: "Avg quiz score", icon: Award, color: "text-amber-600 bg-amber-500/10", suffix: "%" },
  { key: "attendance_rate", label: "Attendance", icon: CalendarCheck, color: "text-cyan-600 bg-cyan-500/10", suffix: "%" },
  { key: "lessons_created", label: "Lessons built", icon: Layers, color: "text-accent bg-accent/10", suffix: "" },
];

export default function Dashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newStudent, setNewStudent] = useState("");
  const [launch, setLaunch] = useState(null);
  const [date, setDate] = useState(new Date());
  const [scheduleLesson, setScheduleLesson] = useState("");
  const [submitTarget, setSubmitTarget] = useState(null);
  const [suggLoading, setSuggLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [subFeedback, setSubFeedback] = useState("");
  const [submittingLib, setSubmittingLib] = useState(false);
  const [usage, setUsage] = useState(null);
  const [checkins, setCheckins] = useState([]);

  const load = async () => {
    try {
      const [s, st, at, ls, qa, lib, us, ci] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/students"),
        api.get("/attendance"),
        api.get("/lessons"),
        api.get("/quiz-attempts"),
        api.get("/library"),
        api.get("/usage"),
        api.get("/checkin-insights"),
      ]);
      setStats(s.data);
      setStudents(st.data);
      setAttendance(at.data);
      setLessons(ls.data);
      setAttempts(qa.data);
      setLibrary(lib.data);
      setUsage(us.data);
      setCheckins(ci.data || []);
    } catch (e) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addStudent = async () => {
    if (!newStudent.trim()) return;
    await api.post("/students", { name: newStudent.trim() });
    setNewStudent("");
    toast.success("Student added");
    load();
  };

  const removeLesson = async (id) => {
    await api.delete(`/lessons/${id}`);
    toast.success("Lesson deleted");
    load();
  };

  const schedule = async () => {
    if (!scheduleLesson) { toast.error("Pick a lesson to schedule"); return; }
    const iso = date.toISOString().slice(0, 10);
    await api.put(`/lessons/${scheduleLesson}`, { scheduled_date: iso, status: "scheduled" });
    toast.success("Lesson scheduled");
    setScheduleLesson("");
    load();
  };

  const openSubmit = async (l) => {
    setSubmitTarget(l);
    setSuggestions([]);
    setSubFeedback("");
    setSuggLoading(true);
    try {
      const { data } = await api.post(`/lessons/${l.id}/library-suggestions`);
      setSuggestions(data.suggestions || []);
    } catch (e) {
      toast.error("Couldn't fetch suggestions — you can still submit");
    } finally {
      setSuggLoading(false);
    }
  };

  const doSubmitLibrary = async () => {
    if (!submitTarget) return;
    setSubmittingLib(true);
    try {
      await api.post(`/lessons/${submitTarget.id}/submit-to-library`, {
        feedback: subFeedback,
        suggestions,
      });
      toast.success("Sent to the command center for library approval");
      setSubmitTarget(null);
      load();
    } catch (e) {
      toast.error("Submission failed");
    } finally {
      setSubmittingLib(false);
    }
  };

  const chartData = attendance.map((a) => ({
    day: new Date(a.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    rate: Math.round((a.present / a.total) * 100),
  }));

  const scheduledDays = lessons.filter((l) => l.scheduled_date).map((l) => new Date(l.scheduled_date + "T00:00:00"));
  const selectedIso = date.toISOString().slice(0, 10);
  const lessonsOnDay = lessons.filter((l) => l.scheduled_date === selectedIso);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight">Welcome back, {user?.name} 👋</h1>
            <p className="text-muted-foreground mt-1">{user?.school || "Your VR classroom at a glance"}</p>
          </div>
          <div className="flex items-center gap-3">
            {user?.id && (
              <button
                data-testid="teacher-class-code"
                onClick={() => { navigator.clipboard?.writeText(user.id.replace(/-/g, "").slice(0, 8).toUpperCase()); toast.success("Class code copied — share it with students"); }}
                className="flex items-center gap-2 rounded-full border border-border bg-card px-4 h-11 hover:bg-secondary transition-colors"
                title="Share this code with students so they can join your class"
              >
                <KeyRound className="w-4 h-4 text-primary" />
                <span className="text-xs text-muted-foreground">Class code</span>
                <span className="font-mono font-bold tracking-widest">{user.id.replace(/-/g, "").slice(0, 8).toUpperCase()}</span>
                <Copy className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            )}
            <Button data-testid="dashboard-new-lesson-btn" onClick={() => nav("/generator")} className="rounded-full h-11 px-6">
              <Sparkles className="w-4 h-4 mr-2" /> New VR lesson
            </Button>
          </div>
        </div>

        {/* Usage meter */}
        {usage && !usage.unlimited && (
          <div className="rounded-2xl border border-border bg-card px-5 py-4 mb-8" data-testid="dashboard-usage-meter">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="font-semibold flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-primary" /> <span className="capitalize">{usage.plan}</span> plan · VR lesson builds this month</span>
              <span className="text-muted-foreground" data-testid="dashboard-usage-count">{usage.used} of {usage.limit} used</span>
            </div>
            <Progress value={Math.min(100, (usage.used / usage.limit) * 100)} className="h-2" />
            {usage.remaining <= 1 && (
              <button data-testid="dashboard-upgrade-link" onClick={() => nav("/pricing")} className="text-xs text-primary font-semibold mt-2.5 flex items-center gap-1 hover:underline">
                <TrendingUp className="w-3.5 h-3.5" /> {usage.remaining <= 0 ? "You're out of builds this month — upgrade for more" : `Only ${usage.remaining} left — upgrade for more builds`}
              </button>
            )}
          </div>
        )}

        {/* Stats */}
        <div data-testid="dashboard-stats-grid" className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {STAT_CARDS.map((c) => (
            <div key={c.key} className="rounded-2xl border border-border bg-card p-5">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${c.color}`}><c.icon className="w-5 h-5" /></div>
              <div className="text-3xl font-display font-extrabold mt-3">{stats?.[c.key] ?? 0}{c.suffix}</div>
              <div className="text-sm text-muted-foreground">{c.label}</div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Attendance chart */}
          <div data-testid="dashboard-attendance-chart" className="lg:col-span-2 rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-5 h-5 text-primary" />
              <h2 className="font-display font-bold text-lg">Attendance — last 14 days</h2>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={chartData} margin={{ left: -20, right: 10, top: 10 }}>
                <defs>
                  <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(244 68% 61%)" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(244 68% 61%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 32% 91%)" vertical={false} />
                <XAxis dataKey="day" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis domain={[0, 100]} fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip formatter={(v) => [`${v}%`, "Present"]} />
                <Area type="monotone" dataKey="rate" stroke="hsl(244 68% 61%)" strokeWidth={2.5} fill="url(#grad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Lesson planner */}
          <div data-testid="dashboard-lesson-planner-calendar" className="rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <CalendarDays className="w-5 h-5 text-primary" />
              <h2 className="font-display font-bold text-lg">Lesson planner</h2>
            </div>
            <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} modifiers={{ scheduled: scheduledDays }}
              modifiersClassNames={{ scheduled: "bg-primary/15 text-primary font-bold rounded-full" }} className="rounded-md" />
            <div className="mt-4 space-y-2">
              {lessonsOnDay.length > 0 ? lessonsOnDay.map((l) => (
                <div key={l.id} className="text-sm flex items-center gap-2 rounded-lg bg-secondary px-3 py-2">
                  <BookOpen className="w-4 h-4 text-primary shrink-0" /> <span className="truncate">{l.title}</span>
                </div>
              )) : <p className="text-sm text-muted-foreground">No lessons scheduled for this day.</p>}
              <div className="flex gap-2 pt-2">
                <Select value={scheduleLesson} onValueChange={setScheduleLesson}>
                  <SelectTrigger data-testid="schedule-lesson-select" className="h-9 text-sm"><SelectValue placeholder="Schedule a lesson…" /></SelectTrigger>
                  <SelectContent>{lessons.map((l) => <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" data-testid="schedule-lesson-btn" onClick={schedule} className="shrink-0"><Plus className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          {/* Students */}
          <div data-testid="dashboard-student-table" className="lg:col-span-2 rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2"><Users className="w-5 h-5 text-primary" /><h2 className="font-display font-bold text-lg">Student roster</h2></div>
              <div className="flex gap-2">
                <Input data-testid="add-student-input" value={newStudent} onChange={(e) => setNewStudent(e.target.value)} placeholder="Add student…" className="h-9 w-40" onKeyDown={(e) => e.key === "Enter" && addStudent()} />
                <Button size="sm" data-testid="add-student-btn" onClick={addStudent}><Plus className="w-4 h-4" /></Button>
              </div>
            </div>
            <div className="max-h-80 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Student</TableHead>
                    <TableHead className="w-40">Progress</TableHead>
                    <TableHead className="text-right">Engagement</TableHead>
                    <TableHead className="text-right hidden sm:table-cell">Completed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {students.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress value={s.progress} className="h-2" />
                          <span className="text-xs text-muted-foreground w-8">{s.progress}%</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant={s.engagement > 75 ? "default" : "secondary"}>{s.engagement}%</Badge>
                      </TableCell>
                      <TableCell className="text-right hidden sm:table-cell">{s.lessons_completed}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Saved lessons */}
          <div data-testid="dashboard-past-lessons-list" className="rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center gap-2 mb-4"><Layers className="w-5 h-5 text-primary" /><h2 className="font-display font-bold text-lg">Saved lessons</h2></div>
            <div className="space-y-3 max-h-80 overflow-auto">
              {lessons.length === 0 && <p className="text-sm text-muted-foreground">No lessons yet. Build your first one!</p>}
              {lessons.map((l) => (
                <div key={l.id} className="rounded-xl border border-border p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm truncate">{l.title}</div>
                      <div className="text-xs text-muted-foreground">{l.subject} · {l.grade}</div>
                      <div className="flex gap-1.5 mt-1.5 flex-wrap">
                        <Badge variant="secondary" className="text-[10px]">{l.style}</Badge>
                        {l.status === "scheduled" && <Badge className="text-[10px] bg-cyan-500/15 text-cyan-700 border-0">Scheduled</Badge>}
                        {l.library_status === "submitted" && <Badge className="text-[10px] bg-amber-500/15 text-amber-700 border-0"><Clock className="w-3 h-3 mr-0.5" />Pending approval</Badge>}
                        {l.library_status === "approved" && <Badge className="text-[10px] bg-emerald-500/15 text-emerald-700 border-0"><CheckCircle2 className="w-3 h-3 mr-0.5" />In library</Badge>}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="secondary" data-testid={`launch-lesson-${l.id}`} onClick={() => setLaunch(l)} className="flex-1 h-8">
                      <PlayCircle className="w-4 h-4 mr-1" /> Launch
                    </Button>
                    <Button size="icon" variant="ghost" data-testid={`print-lesson-${l.id}`} onClick={() => printLesson(l)} className="h-8 w-8">
                      <Printer className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" data-testid={`delete-lesson-${l.id}`} onClick={() => removeLesson(l.id)} className="h-8 w-8 text-destructive">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  {l.library_status !== "approved" && (
                    <Button size="sm" variant="outline" data-testid={`submit-library-${l.id}`} onClick={() => openSubmit(l)} disabled={l.library_status === "submitted"} className="w-full h-8 mt-2 rounded-full">
                      <Send className="w-3.5 h-3.5 mr-1" /> {l.library_status === "submitted" ? "Awaiting approval" : "Submit for library approval"}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recent quiz results */}
      <div data-testid="dashboard-quiz-results" className="max-w-7xl mx-auto mt-6 rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <ClipboardCheck className="w-5 h-5 text-emerald-600" />
          <h2 className="font-display font-bold text-lg">Recent quiz results</h2>
        </div>
        {attempts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No quiz attempts yet. Launch a lesson and tap “Take the quiz” inside the VR world — scores land here automatically.</p>
        ) : (
          <div className="space-y-2 max-h-72 overflow-auto">
            {attempts.map((a) => {
              const good = a.percent >= 60;
              return (
                <div key={a.id} className="flex items-center justify-between gap-3 rounded-xl border border-border px-4 py-2.5">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{a.student_name || "Whole class"}</div>
                    <div className="text-xs text-muted-foreground truncate">{a.lesson_title}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs text-muted-foreground hidden sm:block">{new Date(a.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                    <Badge className={`${good ? "bg-emerald-500/15 text-emerald-700" : "bg-amber-500/15 text-amber-700"} border-0 font-bold`}>
                      {a.score}/{a.total} · {a.percent}%
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pause & ask check-in insights */}
      {checkins.length > 0 && (
        <div data-testid="dashboard-checkin-insights" className="max-w-7xl mx-auto mt-6 rounded-2xl border border-border bg-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <HelpCircle className="w-5 h-5 text-amber-600" />
            <h2 className="font-display font-bold text-lg">Pause &amp; ask insights <span className="text-sm font-normal text-muted-foreground">· how students responded during guided lessons</span></h2>
          </div>
          <div className="space-y-2 max-h-72 overflow-auto">
            {checkins.map((c, i) => (
              <div key={i} className="rounded-xl border border-border px-4 py-2.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{c.prompt || (c.kind === "mcq" ? "Comprehension check" : "Check-in")}</div>
                    <div className="text-xs text-muted-foreground truncate">{c.lesson_title} · Scene {c.scene_index + 1} · Step {c.beat_index + 1}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 text-xs font-semibold">
                    {c.kind === "mcq" ? (
                      <>
                        <span className="text-emerald-600">✓ {c.mcq_correct}</span>
                        <span className="text-red-600">✗ {c.mcq_incorrect}</span>
                      </>
                    ) : (
                      <>
                        <span className="text-emerald-600 flex items-center gap-1"><ThumbsUp className="w-3.5 h-3.5" /> {c.got_it}</span>
                        <span className="text-amber-600">🤔 {c.confused}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Shared library from Educator Cohort */}
      <div data-testid="dashboard-shared-library" className="max-w-7xl mx-auto mt-6 rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <Library className="w-5 h-5 text-primary" />
          <h2 className="font-display font-bold text-lg">Shared library <span className="text-sm font-normal text-muted-foreground">· approved lessons from the Educator Cohort</span></h2>
        </div>
        {library.length === 0 ? (
          <p className="text-sm text-muted-foreground">No shared lessons yet. Approved cohort lessons will appear here for you to reference and adapt.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {library.map((l) => (
              <div key={l.id} className="rounded-xl border border-border p-4">
                <div className="font-semibold text-sm truncate">{l.title}</div>
                <div className="text-xs text-muted-foreground">{l.subject}{l.grade ? ` · ${l.grade}` : ""} · by {l.author}</div>
                {l.content && <p className="text-xs text-muted-foreground mt-2 line-clamp-3">{l.content}</p>}
                {(l.improvements || []).length > 0 && (
                  <Badge variant="secondary" className="mt-2 text-[10px]">{l.improvements.length} approved improvements</Badge>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Launch dialog */}
      <Dialog open={!!launch} onOpenChange={(o) => !o && setLaunch(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>{launch?.title}</DialogTitle>
            <DialogDescription>{launch?.state} · {launch?.grade} · {launch?.subject} · {launch?.topic}</DialogDescription>
          </DialogHeader>
          <div className="rounded-2xl overflow-hidden border border-slate-800 bg-[#0B0F19] h-[440px]" data-testid="launch-vr-container">
            {launch && <VRViewer scene={launch.vr_scene} quiz={launch.documents?.assessment?.questions || []} lessonId={launch.id} students={students} onQuizSubmitted={load} />}
          </div>
        </DialogContent>
      </Dialog>

      {/* Submit for library approval dialog */}
      <Dialog open={!!submitTarget} onOpenChange={(o) => !o && setSubmitTarget(null)}>
        <DialogContent className="max-w-lg" data-testid="submit-library-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Lightbulb className="w-5 h-5 text-primary" /> Submit for library approval</DialogTitle>
            <DialogDescription>{submitTarget?.title} · {submitTarget?.subject} · {submitTarget?.grade}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="text-sm font-semibold mb-2">Suggested edits & improvements</div>
              {suggLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-4"><Loader2 className="w-4 h-4 animate-spin" /> Generating suggestions…</div>
              ) : suggestions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No AI suggestions — you can still submit with your own notes below.</p>
              ) : (
                <ul className="space-y-2" data-testid="library-suggestions-list">
                  {suggestions.map((s, i) => (
                    <li key={i} className="flex items-start gap-2 rounded-lg bg-secondary/60 p-3 text-sm">
                      <Lightbulb className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <div className="text-sm font-semibold mb-1">Notes for reviewers <span className="text-muted-foreground font-normal">(optional)</span></div>
              <Textarea data-testid="library-feedback-input" value={subFeedback} onChange={(e) => setSubFeedback(e.target.value)} placeholder="What would you like feedback on, or what did students respond to?" className="min-h-[70px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSubmitTarget(null)} data-testid="library-cancel-btn">Cancel</Button>
            <Button onClick={doSubmitLibrary} disabled={submittingLib || suggLoading} data-testid="library-submit-btn" className="rounded-full">
              {submittingLib ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Submit to command center</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
